import { Spacer, Text } from "@hackthenorth/north";
import { XCircleIcon } from "@heroicons/react/24/solid";
import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

import { Modal } from "src/shared/components";
import { QRCodeDark } from "src/static/icons";
import { useGooseGamePlayerLazyQuery } from "src/views/goose-games/hackers/graphql/gooseGamePlayer.generated";
import { GGPlayer } from "src/views/goose-games/types";
import { useFindEventAttendeeByBadgeCodeLazyQuery } from "src/views/volunteer/event-redemption/graphql/findEventAttendeeByBadgeCode.generated";
import { useFindEventAttendeeByEmailLazyQuery } from "src/views/volunteer/registration/graphql/findEventAttendeeByEmail.generated";

import FooterActions from "../../../../footerActions";

export interface ScanModalProps {
  isOpen: boolean;
  onConfirm: (player: GGPlayer) => void;
  onCancel: () => void;
}
const ScanModal: React.FC<ScanModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
}) => {
  const [emailInput, setEmailInput] = useState("");
  const [badgeInput, setBadgeInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: null as string | null,
    badge: null as string | null,
  });

  const searchTypeRef = useRef<"email" | "badge" | null>(null);

  const [
    findAttendeeByEmail,
    {
      data: emailData,
      loading: emailLoading,
      error: emailError,
      called: emailCalled,
    },
  ] = useFindEventAttendeeByEmailLazyQuery();
  const [
    findAttendeeByBadgeCode,
    {
      data: badgeData,
      loading: badgeLoading,
      error: badgeError,
      called: badgeCalled,
    },
  ] = useFindEventAttendeeByBadgeCodeLazyQuery();

  const [gooseGamePlayer, { data: playerData }] = useGooseGamePlayerLazyQuery();

  useEffect(() => {
    if (playerData?.gooseGamePlayer) {
      const ggPlayer = playerData.gooseGamePlayer;

      const player: GGPlayer = {
        event_attendee: {
          user: {
            id: ggPlayer.event_attendee.user.id,
            name: ggPlayer.event_attendee?.user?.name ?? "",
            email: ggPlayer.event_attendee?.user?.email ?? "",
          },
          badge_code: ggPlayer.event_attendee?.badge_code ?? null,
        },
        event_attendee_id: ggPlayer.event_attendee_id,
        current_points: ggPlayer.current_points,
        total_points: ggPlayer.total_points,
        created_at: ggPlayer.created_at,
      };
      onConfirm(player);
    }
  }, [playerData, onConfirm]);

  useEffect(() => {
    if (searchTypeRef.current === "email" && emailCalled && !emailLoading) {
      setIsLoading(false);

      const attendee = emailData?.findEventAttendeeByEmail;
      if (attendee) {
        gooseGamePlayer({
          variables: { event_attendee_id: attendee.eventAttendeeId },
        });
      } else {
        setErrors((prev) => ({
          ...prev,
          email: "No hacker found with this email.\n Please try again.",
        }));
      }

      searchTypeRef.current = null;
    }
  }, [
    emailData,
    emailLoading,
    emailError,
    emailCalled,
    onConfirm,
    gooseGamePlayer,
  ]);

  // Handle badge search results
  useEffect(() => {
    if (searchTypeRef.current === "badge" && badgeCalled && !badgeLoading) {
      setIsLoading(false);

      const attendee = badgeData?.findEventAttendeeByBadgeCode;
      if (attendee) {
        gooseGamePlayer({
          variables: { event_attendee_id: attendee.eventAttendeeId },
        });
      } else {
        setErrors((prev) => ({
          ...prev,
          badge: "No hacker found with this ID.\n Please try again.",
        }));
      }

      searchTypeRef.current = null;
    }
  }, [
    badgeData,
    badgeLoading,
    badgeError,
    badgeCalled,
    onConfirm,
    gooseGamePlayer,
  ]);

  const handleSearch = () => {
    setIsLoading(true);
    setErrors({ email: null, badge: null });

    if (emailInput.trim()) {
      searchTypeRef.current = "email";
      findAttendeeByEmail({ variables: { email: emailInput.trim() } });
    } else if (badgeInput.trim()) {
      searchTypeRef.current = "badge";
      findAttendeeByBadgeCode({ variables: { badge_code: badgeInput.trim() } });
    } else {
      setIsLoading(false);
      setErrors({
        email: "Please enter an email or badge ID to search.",
        badge: null,
      });
    }
  };

  return (
    <Modal isOpen={isOpen}>
      <HeaderContainer>
        <HeaderIcon>
          <QRCodeDark />
        </HeaderIcon>
        <Text mods="h3 bold">Scan hacker</Text>
      </HeaderContainer>
      <InputWrapper>
        <Text mods="body bold">Search by Email</Text>
        <Input
          type="text"
          placeholder="Enter hacker email here"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          disabled={isLoading}
        />
        {errors.email && (
          <WarningWrapper>
            <WarningIcon>
              <XCircleIcon color="#D95858" width="18px" />
            </WarningIcon>
            <Text
              mods="body textDanger"
              style={{ whiteSpace: "pre-line", lineHeight: "1.5" }}
            >
              {errors.email}
            </Text>
          </WarningWrapper>
        )}
      </InputWrapper>
      <OrWrapper>
        <OrLine />
        <Text mods="body borderGray caption">OR</Text>
        <OrLine />
      </OrWrapper>
      <InputWrapper>
        <Text mods="body bold">Search by Badge ID</Text>
        <Input
          type="text"
          placeholder="Input badge ID here"
          value={badgeInput}
          onChange={(e) => setBadgeInput(e.target.value)}
          disabled={isLoading}
        />
        {errors.badge && (
          <WarningWrapper>
            <WarningIcon>
              <XCircleIcon color="#D95858" width="18px" />
            </WarningIcon>
            <Text
              mods="body textDanger"
              style={{ whiteSpace: "pre-line", lineHeight: "1.5" }}
            >
              {errors.badge}
            </Text>
          </WarningWrapper>
        )}
      </InputWrapper>
      <Spacer height={36} />
      <FooterActions
        confirmText={isLoading ? "Searching..." : "Search"}
        onConfirm={handleSearch}
        onCancel={onCancel}
        isConfirmDisabled={isLoading || (!emailInput && !badgeInput)}
      />
    </Modal>
  );
};

export default ScanModal;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 24px;
  gap: 8px;
`;

const HeaderIcon = styled.div<{ backgroundColor?: string }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 50%;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;

  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }

  &:disabled {
    background-color: ${({ theme }) =>
      theme.globalConstants.color.backgroundLight};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const OrWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 16px 0;
`;

const OrLine = styled.div`
  width: 100%;
  margin: 0 8px;
  height: 1px;
  background-color: ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const WarningWrapper = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  align-items: start;
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundDangerLight};
`;

const WarningIcon = styled.div`
  padding: 4px 0;
`;
